// Chart utilities for D3.js visualizations
// Converted from frontend/lib/chart-utils.js

// D3 is loaded globally via CDN, so we need to handle both browser and server environments
declare global {
  interface Window {
    d3: any;
  }
}

// In browser environment, D3 is loaded from CDN as a global
// In server/build environment, we skip it
let d3: any;
if (typeof window !== 'undefined') {
  // In browser, use global d3 from CDN
  d3 = (window as any).d3;
} else {
  // In server/build environment, create a stub
  // The actual runtime will use the global d3 from CDN
  d3 = undefined;
}

interface ChartColors {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  gray: string;
  primaryLight?: string;
  successLight?: string;
  warningLight?: string;
  dangerLight?: string;
}

interface ChartMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface ChartDimensions {
  width: number;
  height: number;
  innerWidth: number;
  innerHeight: number;
}

export class ChartUtils {
  public colors: ChartColors;
  public colorScale: any; // d3.ScaleOrdinal<string, string>
  private fallbackColors: ChartColors;

  constructor() {
    // Fallback colors for browsers without CSS custom property support
    this.fallbackColors = {
      primary: '#6366f1',
      secondary: '#8b5cf6',
      success: '#059669',
      warning: '#d97706',
      danger: '#dc2626',
      info: '#0284c7',
      gray: '#64748b'
    };

    // Get colors from CSS custom properties to match design system
    this.colors = this.getCSSColors();
    
    this.colorScale = d3 && d3.scaleOrdinal ? d3.scaleOrdinal<string>()
      .range(Object.values(this.colors)) : null;
  }

  // Get colors from CSS custom properties to match design system
  private getCSSColors(): ChartColors {
    if (typeof window === 'undefined') {
      return this.fallbackColors;
    }

    const root = document.documentElement;
    const getColor = (property: string, fallback: string): string => {
      const cssColor = getComputedStyle(root).getPropertyValue(property).trim();
      return cssColor || fallback;
    };

    return {
      primary: getColor('--primary-color', '#6366f1'),
      secondary: getColor('--secondary-color', '#8b5cf6'),
      success: getColor('--success-color', '#059669'),
      warning: getColor('--warning-color', '#d97706'),
      danger: getColor('--danger-color', '#dc2626'),
      info: getColor('--info-color', '#0284c7'),
      gray: getColor('--gray-500', '#64748b'),
      // Additional colors for better chart variety
      primaryLight: getColor('--primary-light', '#eef2ff'),
      successLight: getColor('--success-light', '#ecfdf5'),
      warningLight: getColor('--warning-light', '#fffbeb'),
      dangerLight: getColor('--danger-light', '#fef2f2')
    };
  }

  // Method to refresh colors if CSS variables change
  refreshColors(): void {
    this.colors = this.getCSSColors();
    if (this.colorScale && this.colorScale.range) {
      this.colorScale.range(Object.values(this.colors));
    }
  }

  // Calculate chart dimensions with margins
  calculateDimensions(
    containerWidth: number, 
    containerHeight: number, 
    margins: ChartMargins
  ): ChartDimensions {
    const width = containerWidth;
    const height = containerHeight;
    const innerWidth = Math.max(0, width - margins.left - margins.right);
    const innerHeight = Math.max(0, height - margins.top - margins.bottom);

    return {
      width,
      height,
      innerWidth,
      innerHeight
    };
  }

  // Create responsive SVG with viewBox
  createResponsiveSVG(
    container: any, // d3.Selection<any, any, any, any>
    width: number,
    height: number,
    margins: ChartMargins
  ): any { // d3.Selection<SVGSVGElement, any, any, any>
    // Remove existing SVG
    container.select('svg').remove();

    const svg = container
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', '100%');

    // Add main group with margins
    const g = svg
      .append('g')
      .attr('transform', `translate(${margins.left},${margins.top})`);

    return svg;
  }

  // Format numbers for display
  formatNumber(value: number, type: 'compact' | 'currency' | 'percentage' | 'full' = 'compact'): string {
    if (typeof window === 'undefined') {
      return value.toString();
    }

    const formatter = new Intl.NumberFormat('en-US', {
      ...(type === 'compact' && { notation: 'compact', compactDisplay: 'short' }),
      ...(type === 'currency' && { style: 'currency', currency: 'USD' }),
      ...(type === 'percentage' && { style: 'percent', minimumFractionDigits: 1 }),
      ...(type === 'full' && { maximumFractionDigits: 2 })
    });

    return formatter.format(value);
  }

  // Format dates for display
  formatDate(date: Date, format: 'short' | 'medium' | 'long' = 'medium'): string {
    if (typeof window === 'undefined') {
      return date.toISOString();
    }

    const options: Intl.DateTimeFormatOptions = {
      short: { month: 'short', day: 'numeric' },
      medium: { month: 'short', day: 'numeric', year: 'numeric' },
      long: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }
    }[format];

    return new Intl.DateTimeFormat('en-US', options).format(date);
  }

  // Create tooltip
  createTooltip(): any { // d3.Selection<HTMLDivElement, any, any, any>
    if (typeof window === 'undefined' || !d3 || !d3.select) {
      return null; // No D3 available
    }

    return d3.select('body')
      .append('div')
      .attr('class', 'chart-tooltip')
      .style('position', 'absolute')
      .style('padding', '12px')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('border-radius', '8px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('font-size', '12px')
      .style('box-shadow', '0 4px 6px -1px rgba(0, 0, 0, 0.1)')
      .style('backdrop-filter', 'blur(8px)')
      .style('z-index', '1000');
  }

  // Show tooltip
  showTooltip(
    tooltip: any, // d3.Selection<HTMLDivElement, any, any, any>
    content: string,
    event: MouseEvent
  ): void {
    if (typeof window === 'undefined') return;

    tooltip
      .style('opacity', 1)
      .html(content)
      .style('left', `${event.pageX + 10}px`)
      .style('top', `${event.pageY - 10}px`);
  }

  // Hide tooltip
  hideTooltip(tooltip: any): void { // d3.Selection<HTMLDivElement, any, any, any>
    tooltip.style('opacity', 0);
  }

  // Create color scale for categorical data
  createCategoricalColorScale(domain: string[]): any { // d3.ScaleOrdinal<string, string>
    if (!d3 || !d3.scaleOrdinal) {
      console.warn('D3.js not available for creating color scale');
      return null;
    }
    return d3.scaleOrdinal<string>()
      .domain(domain)
      .range(Object.values(this.colors));
  }

  // Create gradient definitions
  createGradients(
    svg: any, // d3.Selection<SVGSVGElement, any, any, any>
    gradientId: string,
    colors: string[]
  ): void {
    const defs = svg.select('defs').empty() ? svg.append('defs') : svg.select('defs');
    
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0).attr('y1', 0)
      .attr('x2', 0).attr('y2', 1);

    gradient.selectAll('stop')
      .data(colors)
      .enter().append('stop')
      .attr('offset', (d, i) => i / (colors.length - 1))
      .attr('stop-color', d => d);
  }

  // Animate chart entrance
  animateEntrance(
    selection: any, // d3.Selection<any, any, any, any>
    duration: number = 800
  ): any { // d3.Transition<any, any, any, any>
    return selection
      .style('opacity', 0)
      .transition()
      .duration(duration)
      .ease(d3 && d3.easeBackOut ? d3.easeBackOut.overshoot(1.2) : undefined)
      .style('opacity', 1);
  }

  // Default chart margins
  get defaultMargins(): ChartMargins {
    return {
      top: 20,
      right: 20,
      bottom: 40,
      left: 40
    };
  }

  // Responsive breakpoints
  get breakpoints() {
    return {
      mobile: 640,
      tablet: 768,
      desktop: 1024,
      wide: 1280
    };
  }
}

// Export singleton instance
export const chartUtils = new ChartUtils();